import React, {useState, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useSelector, useDispatch} from "react-redux";

//helpers:
import {getTranslatedGroupName} from "../../../../../_metronic/_helpers";

//actions:
import {
    selectAllGroups,
    selectGroupsStatus,
    selectCredentialsStatus,
    updateCredential,
} from "../../../../redux_slices/settingsCredentialsSlice";

//components:
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import {Dropdown} from "react-bootstrap";
import {CustomTooltip} from "../../../../widgets";
import {CircularProgress} from "@material-ui/core";
import HighlightOffRoundedIcon from "@material-ui/icons/HighlightOffRounded";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";

const useTableDetails = () => {
    const {t} = useTranslation();

    //redux:
    const dispatch = useDispatch();
    const allGroups = useSelector(selectAllGroups);
    const {type, error} = useSelector(selectGroupsStatus);
    const credentialsStatus = useSelector(selectCredentialsStatus);

    const isLoadingCredentials = () => credentialsStatus.type === "loading";

    //local state:
    const [updatingRowId, setUpdatingRowId] = useState(0);

    const onUserChange = useCallback(async credential => {
        if (credential.credential_id) {
            try {
                setUpdatingRowId(credential.credential_id);
                dispatch(updateCredential(credential));
                setUpdatingRowId(0);
            } catch (err) {
                console.error("Failed to save the credential company: ", err);
            }
        }
    }, []);

    const renderDropdown = (row = {}) => {
        const {groups} = row;

        const isLoadingDropdown = () => type === "idle" || type === "loading";

        if (isLoadingDropdown())
            return (
                <div className="d-flex w-100 h-100">
                    <CircularProgress className="m-auto" />
                </div>
            );
        else if (error?.text) return <div className="alert alert-light text-dark w-fit-content">{error.text}</div>;

        return (
            <Dropdown>
                <CustomTooltip
                    config={{
                        text: t("ADMINPANEL.USERS.TYPE.SELECTOR.TOOLTIP"),
                        placement: "top",
                    }}
                >
                    <Dropdown.Toggle variant="light">
                        {getTranslatedGroupName(t, groups?.[0])} <KeyboardArrowDownIcon />
                    </Dropdown.Toggle>
                </CustomTooltip>

                <Dropdown.Menu className="tables_dropdown_list p-2">
                    {allGroups?.length ? (
                        allGroups.map((group, i) => (
                            <Dropdown.Item
                                key={i}
                                className="dropdown-item elipsis_1line"
                                onClick={() => onUserChange({...row, groups: [group]})}
                                active={
                                    !groups?.[0]?.group_id
                                        ? group.group_id === 1
                                        : group.group_id === groups?.[0]?.group_id
                                }
                            >
                                {getTranslatedGroupName(t, group)}
                            </Dropdown.Item>
                        ))
                    ) : (
                        <span>{t("MODAL.INVITE_USERS.DROPDOWN.PLACEHOLDER")}</span>
                    )}
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    const formatRow = (row, i) => {
        const {credential_id = 0, name = "", avatar_url = "", username = ""} = row;

        return (
            <TableRow
                key={i}
                className={`${
                    isLoadingCredentials === "loading" && updatingRowId === credential_id ? "row_updating" : ""
                }`}
            >
                <TableCell align="left" component="th" scope="row" style={{minWidth: 200, maxWidth: 400}}>
                    <div className="d-flex align-items-center">
                        <div
                            className="w-40px h-40px my-4 mr-4 rounded-circle border border-gray shadow bg_image_cover"
                            style={{backgroundImage: `url(${avatar_url || "/media/avatars/dummy-avatar.jpg"})`}}
                        ></div>
                        <div className="d-flex flex-column">
                            <div className="d-flex flex-column w-100 text-dark-75 font-size-base mb-0">
                                {name}
                                <p>
                                    <span className="text-primary mb-0 mt-1">{username && username.length > 0 ? username : <>ID: {row.external_sub}</>}</span>
                                    {row.external_iss && !row.external_iss.includes("cognito") ?
                                        <span className="text-dark ml-1 mb-0 mt-1">{t('ADMINPANEL.USERS.INTEGRATION')}</span> : null}
                                </p>
                            </div>
                        </div>
                    </div>
                </TableCell>

                <TableCell align="left" className="mb-0 font-size-1rem">
                    <div className="d-flex align-items-center">{renderDropdown(row)}</div>
                </TableCell>

                {/* actions: */}
                <TableCell align="right">
                    <CustomTooltip
                        config={{
                            text: t("ADMINPANEL.USERS.TABLE.DEACTIVATE.TOOLTIP"),
                            placement: "top",
                        }}
                    >
                        <button
                            type="button"
                            className="close ml-auto font-size-base"
                            aria-label="Close"
                            onClick={() => onUserChange({...row, deleted_at: new Date().getTime()})}
                        >
                            <HighlightOffRoundedIcon />
                        </button>
                    </CustomTooltip>
                </TableCell>
            </TableRow>
        );
    };

    return {
        formatRow,
    };
};

export default useTableDetails;
